import styled from '@emotion/styled'
import { ImageField } from '@prismicio/client'
import { ScrollOverMediaDocumentDataBodyImagePanoramaStepSlicePrimary } from '../../../../../types.generated'
import { useIsPortrait } from '../../../../util/breakpoints'
import { LazyImage } from '../../../atoms/LazyImage'
import isPropValid from '@emotion/is-prop-valid'
import { useAppRootCompRef } from '../../../../AppContext'
import { VIEWPORT_HEIGHT } from '../../../../util/viewportHeight'

type ImageLandscapeType =
  ScrollOverMediaDocumentDataBodyImagePanoramaStepSlicePrimary['image_landscape']
type ImagePortraitType =
  ScrollOverMediaDocumentDataBodyImagePanoramaStepSlicePrimary['image_landscape']
type PanoramaDirection =
  ScrollOverMediaDocumentDataBodyImagePanoramaStepSlicePrimary['direction']

const StyledImage = styled(LazyImage, {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{
  direction: PanoramaDirection
  totalPixelToScroll: number
  scrollPercentage: number
}>`
  position: relative;
  top: 0;
  ${({ totalPixelToScroll, scrollPercentage, direction }) =>
    direction === 'left-to-right' &&
    `left: ${-(totalPixelToScroll * scrollPercentage)}px;`}
  ${({ totalPixelToScroll, scrollPercentage, direction }) =>
    direction === 'right-to-left' &&
    `right: ${(totalPixelToScroll * (1 - scrollPercentage))}px;`}
  height: ${VIEWPORT_HEIGHT};
  width: auto;
`

interface PanoramaImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  imageLandscape: ImageLandscapeType
  imagePortrait: ImagePortraitType
  scrollPercentage: number
  direction: PanoramaDirection
}

const decideImage = (
  imageLandscape: ImageLandscapeType,
  imagePortrait: ImagePortraitType,
  isPortrait: boolean
): ImageField => {
  if (isPortrait && imagePortrait?.url) {
    return imagePortrait
  } else {
    return imageLandscape
  }
}

export const PanoramaImage = (props: PanoramaImageProps) => {
  const isPortrait = useIsPortrait()
  const image = decideImage(
    props.imageLandscape,
    props.imagePortrait,
    isPortrait
  )

  const appRootCompRef = useAppRootCompRef()
  const appContainerWidth = appRootCompRef?.current?.clientWidth

  let totalPixelToScroll = 0
  if (appContainerWidth !== undefined && image.dimensions) {
    const scaleFactor = window.innerHeight / image.dimensions.height
    const renderedWidth = image.dimensions.width * scaleFactor

    totalPixelToScroll = renderedWidth - appContainerWidth
  }

  return (
    <StyledImage
      src={image.url ?? ''}
      alt={image.alt ?? ''}
      {...props}
      totalPixelToScroll={totalPixelToScroll}
      scrollPercentage={props.scrollPercentage}
    />
  )
}
